import React, { useCallback, useState } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { makeStyles } from 'tss-react/mui';
import classNames from 'classnames';

interface ButtonConfig {
  name: string;
  label: string;
  onClick?: () => void;
}

interface ButtonGroupSelectedProps {
  buttons: ButtonConfig[];
  className?: string;
}

const useStyles = makeStyles<ButtonGroupSelectedProps>()((theme, { buttons }) => ({
  root: {
    position: 'absolute',
    backgroundColor: 'white',
    right: 10,
    bottom: 20,
    zIndex: 1001,
    [theme.breakpoints.down('md')]: {
      position: 'initial',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 0,
      '& > *': {
        borderRadius: 0,
        boxShadow: 'none !important',
        flexBasis: `calc(100% / ${buttons.length})`,
      },
      '& > *:first-child': {
        borderLeftWidth: 0,
      },
      '& > *:last-child': {
        borderRightWidth: 0,
      },
    },
  },
  '@media print': {
    root: {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },
}));

const ButtonGroupSelected = (props: ButtonGroupSelectedProps) => {
  const { buttons, className } = props;
  const { classes } = useStyles(props);
  const [selectedButton, setSelectedButton] = useState(buttons[0].name);

  const handleClick = useCallback(
    (button: ButtonConfig) => {
      if (selectedButton !== button.name) {
        if (button.onClick) {
          button.onClick();
        }
        setSelectedButton(button.name);
      }
    },
    [selectedButton],
  );

  return (
    <ButtonGroup className={classNames(classes.root, className)}>
      {buttons.map((button) => {
        return (
          <Button
            key={button.name}
            variant={selectedButton === button.name ? 'contained' : undefined}
            onClick={() => handleClick(button)}
          >
            {button.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

export default ButtonGroupSelected;
